import { defaultIsOk, wait } from "src/utils/extension/ServiceClient";

export type RateLimitFetchResult = {
  ok: boolean;
  status: number;
  /** Final response URL after redirects, when the transport provides it. */
  url?: string;
  data?: unknown;
};

export const IntegrationRateLimitWaitSchedule = [10_000, 30_000, 60_000];

export type RateLimitFetchFn<T extends RateLimitFetchResult> = (
  url: string,
  opts?: RequestInit
) => Promise<T>;

export interface FetchWithRateLimitGateOptions {
  /** Max number of retries after the initial request (default: waitSchedule.length). */
  retries?: number;
  /** Delay in ms before each retry attempt (index 0 = first retry after rate limit). */
  waitSchedule: number[];
  isOk?: (res: RateLimitFetchResult) => boolean;
  /** Defaults to HTTP 429. */
  isRateLimited?: (res: RateLimitFetchResult) => boolean;
  /** Runs after the wait and before the next fetch attempt (owner recovery only). */
  beforeRetry?: (lastRes?: RateLimitFetchResult) => Promise<void>;
  label?: string;
}

function defaultIsRateLimited(res: RateLimitFetchResult) {
  return res.status === 429;
}

/**
 * Shared rate-limit wait gate: parallel callers block on one recovery owner
 * while it waits (per waitSchedule) and retries. Other waiters then re-issue
 * their own request.
 */
export class RateLimitGate {
  private rateLimitRecovery?: Promise<void>;

  private async recoverFromRateLimit<T extends RateLimitFetchResult>(
    fetchFn: RateLimitFetchFn<T>,
    url: string,
    opts: RequestInit,
    {
      retries,
      waitSchedule,
      isOk,
      isRateLimited,
      beforeRetry,
      label,
    }: Required<
      Pick<
        FetchWithRateLimitGateOptions,
        "retries" | "waitSchedule" | "isOk" | "isRateLimited"
      >
    > &
      Pick<FetchWithRateLimitGateOptions, "beforeRetry"> & { label: string },
    nextAttempt: number,
    initialRes?: T
  ): Promise<T> {
    let lastRes: T | undefined = initialRes;

    for (let attempt = nextAttempt; attempt <= retries; attempt++) {
      const waitMs =
        waitSchedule[attempt - 1] ?? waitSchedule[waitSchedule.length - 1] ?? 0;
      console.warn(`(${label}) request rate limited, waiting ${waitMs}ms`, {
        url,
        responseUrl: lastRes?.url,
        status: lastRes?.status,
        retry: attempt,
        max: retries,
      });
      await wait(waitMs);
      if (beforeRetry) await beforeRetry(lastRes);

      lastRes = await fetchFn(url, opts);
      if (isOk(lastRes)) return lastRes;
      if (!isRateLimited(lastRes) || attempt >= retries) return lastRes;
    }

    return lastRes!;
  }

  async fetchWithRateLimitGate<T extends RateLimitFetchResult>(
    fetchFn: RateLimitFetchFn<T>,
    url: string,
    opts: RequestInit = {},
    {
      waitSchedule,
      retries = waitSchedule.length,
      isOk = defaultIsOk,
      isRateLimited = defaultIsRateLimited,
      beforeRetry,
      label = "client",
    }: FetchWithRateLimitGateOptions
  ): Promise<T> {
    let lastRes: T | undefined;

    for (let attempt = 0; attempt <= retries; attempt++) {
      const activeRecovery = this.rateLimitRecovery;
      if (activeRecovery) await activeRecovery;

      lastRes = await fetchFn(url, opts);
      if (isOk(lastRes)) return lastRes;
      if (!isRateLimited(lastRes) || attempt >= retries) return lastRes;

      if (this.rateLimitRecovery) {
        await this.rateLimitRecovery;
        continue;
      }

      const recovery = this.recoverFromRateLimit(
        fetchFn,
        url,
        opts,
        {
          retries,
          waitSchedule,
          isOk,
          isRateLimited,
          beforeRetry,
          label,
        },
        attempt + 1,
        lastRes
      );
      const gatedRecovery = recovery
        .then(
          () => undefined,
          () => undefined
        )
        .finally(() => {
          if (this.rateLimitRecovery === gatedRecovery) {
            this.rateLimitRecovery = undefined;
          }
        });
      this.rateLimitRecovery = gatedRecovery;
      return await recovery;
    }

    return lastRes!;
  }
}
